import CryptoJS from 'crypto-js';
import { encode as base64Encode, decode as base64Decode } from 'base-64';

// Secret key for encryption - in production, this should be stored securely
const SECRET_KEY = 'UNO_MOBILE_SECRET_2024';

/**
 * Encrypts a phone number for use in deep links
 * @param {string} phoneNumber - The phone number to encrypt (without country code)
 * @returns {string} - Encrypted and base64 encoded string
 */
export const encryptPhoneNumber = (phoneNumber) => {
  try {
    // Remove any non-digit characters
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    
    // Add some padding and timestamp for additional security
    const timestamp = Date.now().toString();
    const payload = `${cleanPhone}|${timestamp}`;
    
    // Encrypt using AES
    const encrypted = CryptoJS.AES.encrypt(payload, SECRET_KEY).toString();
    
    // Make it URL safe by replacing problematic characters
    const urlSafe = encrypted
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '.');
    
    return urlSafe;
  } catch (error) {
    console.error('Phone encryption error:', error);
    return null;
  }
};

/**
 * Decrypts a phone number from deep link parameter
 * @param {string} encryptedPhone - The encrypted phone number string
 * @returns {string|null} - Decrypted phone number or null if failed
 */
export const decryptPhoneNumber = (encryptedPhone) => {
  try {
    // Restore original base64 format
    const restored = encryptedPhone
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .replace(/\./g, '=');
    
    // Decrypt using AES
    const decrypted = CryptoJS.AES.decrypt(restored, SECRET_KEY);
    const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
    
    if (!decryptedText) {
      console.error('Failed to decrypt phone number');
      return null;
    }
    
    // Extract phone number from payload
    const [phoneNumber, timestamp] = decryptedText.split('|');
    
    // Optional: Check if timestamp is not too old (e.g., 24 hours)
    const now = Date.now();
    const linkTimestamp = parseInt(timestamp);
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    
    if (now - linkTimestamp > maxAge) {
      console.warn('Encrypted phone link has expired');
      return null;
    }
    
    // Validate phone number format (should be 10 digits)
    if (!/^\d{10}$/.test(phoneNumber)) {
      console.error('Invalid phone number format after decryption');
      return null;
    }
    
    return phoneNumber;
  } catch (error) {
    console.error('Phone decryption error:', error);
    return null;
  }
};

/**
 * Generates a complete encrypted deep link for WhatsApp
 * @param {string} phoneNumber - The phone number to encrypt
 * @returns {string} - Complete deep link URL
 */
export const generateAutoLoginLink = (phoneNumber) => {
  const encrypted = encryptPhoneNumber(phoneNumber);
  if (!encrypted) {
    return null;
  }
  return `unodriver://auth/${encrypted}`;
};

/**
 * Simple fallback encryption for cases where crypto-js might not be available
 * Uses XOR cipher with base64 encoding
 * @param {string} phoneNumber - Phone number to encrypt
 * @returns {string} - Encrypted string
 */
export const simpleEncryptPhoneNumber = (phoneNumber) => {
  try {
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    const key = 'UNOKEY2024';
    let encrypted = '';
    
    for (let i = 0; i < cleanPhone.length; i++) {
      const phoneChar = cleanPhone.charCodeAt(i);
      const keyChar = key.charCodeAt(i % key.length);
      encrypted += String.fromCharCode(phoneChar ^ keyChar);
    }
    
    // Base64 encode and make URL safe
    const base64 = base64Encode(encrypted);
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '.');
  } catch (error) {
    console.error('Simple encryption error:', error);
    return null;
  }
};

/**
 * Simple fallback decryption
 * @param {string} encryptedPhone - Encrypted phone string
 * @returns {string|null} - Decrypted phone number
 */
export const simpleDecryptPhoneNumber = (encryptedPhone) => {
  try {
    // Restore base64 format
    const restored = encryptedPhone
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .replace(/\./g, '=');
    
    const decoded = base64Decode(restored);
    const key = 'UNOKEY2024';
    let decrypted = '';
    
    for (let i = 0; i < decoded.length; i++) {
      const encryptedChar = decoded.charCodeAt(i);
      const keyChar = key.charCodeAt(i % key.length);
      decrypted += String.fromCharCode(encryptedChar ^ keyChar);
    }
    
    // Validate phone number format
    if (!/^\d{10}$/.test(decrypted)) {
      console.error('Invalid phone number format after simple decryption');
      return null;
    }
    
    return decrypted;
  } catch (error) {
    console.error('Simple decryption error:', error);
    return null;
  }
};

export default {
  encryptPhoneNumber,
  decryptPhoneNumber,
  generateAutoLoginLink,
  simpleEncryptPhoneNumber,
  simpleDecryptPhoneNumber,
}; 